import { useCallback, useEffect, useMemo, useState } from "react";
import { PriceChart } from "../components/PriceChart";
import { SymbolLabel } from "../components/SymbolLabel";
import type { DataSource } from "../api/dataSource";
import {
  fmtPct,
  fmtPx,
  fmtQtyByClass,
  fmtSigned,
  fmtUSD,
  qtyUnitLabel,
} from "../lib/format";
import { assetClassOf } from "../lib/assetClass";
import { AccountBadge, accountDisplayFor } from "../lib/accountDisplay";
import type {
  Account,
  ChartSnapshot,
  ChartBar,
  ChartLevel,
  ChartTimeframe,
  LivePnLEntry,
  LivePnLSnapshot,
  Position,
  PositionReassessment,
} from "../types";

// Narrow DataSource subset — DayTradesScreen only needs these
// methods. Accepting the subset (instead of the full DataSource)
// keeps the prop compatible with both the raw DataSource and the
// useDataSource() wrapper (which exposes the same method shapes).
type TradesDataSource = Pick<
  DataSource,
  | "getChartSnapshot"
  | "getLivePnL"
  | "tightenStop"
  | "adjustTarget"
  | "breakEven"
  | "exitAtMarket"
  | "exitAtLimit"
  | "adjustPositionQty"
> &
  Partial<
    Pick<
      DataSource,
      | "listPositionReassessments"
      | "runPositionReassessment"
      | "confirmPositionReassessmentAction"
      | "dismissPositionReassessment"
    >
  >;

type Props = {
  accounts: Account[];
  positions: Position[];
  positionReassessments?: PositionReassessment[];
  ds: TradesDataSource;
};

type PendingAction =
  | { kind: "tighten_stop"; preset: PresetKey; newStop: string }
  | { kind: "adjust_target"; newTarget: string }
  | { kind: "break_even" }
  | { kind: "exit_at_market" }
  | { kind: "exit_at_limit"; limitPrice: string }
  | { kind: "adjust_qty"; targetRiskDollars: string };

type PresetKey =
  | "break_even"
  | "plus_0_5r"
  | "plus_1r"
  | "trailing_2_atr"
  | "custom";

const LIVE_REFRESH_MS = 1000;
// Each live window backfills from the intraday chart endpoint at a
// bar cadence coarse enough to keep the bar count sane, then merges
// the 1s live-poll points into the SAME bucket grid so x-spacing
// stays uniform for the renderer.
const LIVE_ZOOM_WINDOWS = [
  {
    label: "1h",
    seconds: 60 * 60,
    backfillTimeframe: "1m" as ChartTimeframe,
    bucketSeconds: 60,
  },
  {
    label: "4h",
    seconds: 4 * 60 * 60,
    backfillTimeframe: "5m" as ChartTimeframe,
    bucketSeconds: 5 * 60,
  },
  {
    label: "1D",
    seconds: 24 * 60 * 60,
    backfillTimeframe: "15m" as ChartTimeframe,
    bucketSeconds: 15 * 60,
  },
] as const;
const DEFAULT_LIVE_ZOOM_INDEX = 1;
const MAX_LIVE_WINDOW_SECONDS =
  LIVE_ZOOM_WINDOWS[LIVE_ZOOM_WINDOWS.length - 1].seconds;
const CRYPTO_BASES = new Set([
  "AAVE",
  "ADA",
  "AVAX",
  "BAT",
  "BCH",
  "BTC",
  "DOGE",
  "DOT",
  "ETH",
  "LINK",
  "LTC",
  "MKR",
  "SHIB",
  "SOL",
  "SUSHI",
  "UNI",
  "USDC",
  "USDT",
  "XTZ",
  "YFI",
  "XRP",
]);

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "break_even", label: "Break-even" },
  { key: "plus_0_5r", label: "+0.5R" },
  { key: "plus_1r", label: "+1R" },
  { key: "trailing_2_atr", label: "Trail ~1%" },
];

const fmtMaybePx = (value: number): string =>
  Number.isFinite(value) && value > 0 ? fmtPx(value) : "n/a";

const editablePrice = (value: number): string =>
  Number.isFinite(value) && value > 0 ? value.toFixed(value < 10 ? 4 : 2) : "";

const positionSideLabel = (position: Position): string =>
  position.side === "short" ? "Short" : "Long";

const positionKey = (position: Pick<Position, "brokerAccountId" | "sym">): string =>
  `${position.brokerAccountId}:${position.sym.toUpperCase()}`;

// Phase 9-UI: classification is assetClass-first (server-resolved),
// falling back to the crypto symbol heuristics for stale rows —
// see lib/assetClass.ts. Forex renders whole, thousands-grouped units.
const positionQtyClass = (
  position: Pick<Position, "sym"> & { assetClass?: string | null },
): "equity" | "crypto" | "forex" => assetClassOf(position);

const formatQty = (
  qty: number,
  position: Pick<Position, "sym"> & { assetClass?: string | null },
): string => {
  if (!Number.isFinite(qty)) return "n/a";
  return fmtQtyByClass(qty, positionQtyClass(position));
};

const positionUnit = (
  position: Pick<Position, "sym"> & { assetClass?: string | null },
): string => qtyUnitLabel(positionQtyClass(position));

const isActiveExitStatus = (status?: string | null): boolean => {
  if (!status) return true;
  return [
    "accepted",
    "held",
    "new",
    "open",
    "partial",
    "partially_filled",
    "pending",
    "pending_new",
    "pending_replace",
    "replaced",
    "working",
  ].includes(status.toLowerCase());
};

const isPositionInProfit = (
  position: Position,
  currentPrice: number,
): boolean =>
  position.side === "short"
    ? currentPrice < position.entry
    : currentPrice > position.entry;

const positionOutcomeAtExit = (
  position: Position,
  exitPrice: number,
): number => {
  if (
    !Number.isFinite(exitPrice) ||
    exitPrice <= 0 ||
    !Number.isFinite(position.entry) ||
    position.entry <= 0 ||
    !Number.isFinite(position.qty) ||
    position.qty <= 0
  ) {
    return 0;
  }
  const pnlPerUnit =
    position.side === "short"
      ? position.entry - exitPrice
      : exitPrice - position.entry;
  return pnlPerUnit * position.qty;
};

const currentPositionRisk = (
  position: Position,
  currentPrice: number,
): number => {
  const riskPerShare =
    position.side === "short"
      ? position.stop - currentPrice
      : currentPrice - position.stop;
  return Math.max(0, riskPerShare * position.qty);
};

const previewTargetQty = (
  position: Position,
  currentPrice: number,
  targetRiskDollars: number,
): {
  newQty: number;
  action: "add" | "trim";
  targetGain: number;
} | null => {
  const riskPerShare =
    position.side === "short"
      ? position.stop - currentPrice
      : currentPrice - position.stop;
  const gainPerShare =
    position.side === "short"
      ? currentPrice - position.target
      : position.target - currentPrice;
  if (
    !Number.isFinite(targetRiskDollars) ||
    targetRiskDollars <= 0 ||
    !Number.isFinite(riskPerShare) ||
    !Number.isFinite(gainPerShare) ||
    riskPerShare <= 0 ||
    gainPerShare <= 0
  ) {
    return null;
  }
  const rawQty = targetRiskDollars / riskPerShare;
  const newQty = positionQtyClass(position) === "crypto"
    ? Math.floor(rawQty * 100_000_000) / 100_000_000
    : Math.floor(rawQty);
  if (newQty <= 0) return null;
  return {
    newQty,
    action: newQty >= position.qty ? "add" : "trim",
    targetGain: newQty * gainPerShare,
  };
};

export const DayTradesScreen = ({
  accounts,
  positions,
  positionReassessments = [],
  ds,
}: Props) => {
  const [accountFilter, setAccountFilter] = useState<
    "all" | "live" | "paper" | "crypto" | "forex"
  >("all");
  const [selectedPositionKey, setSelectedPositionKey] = useState<string | null>(
    positions.length > 0 ? positionKey(positions[0]) : null,
  );
  const [chart, setChart] = useState<ChartSnapshot | null>(null);
  const [livePnL, setLivePnL] = useState<LivePnLSnapshot | null>(null);
  const [liveBarsBySymbol, setLiveBarsBySymbol] = useState<
    Record<string, ChartBar[]>
  >({});
  const [liveZoomIndex, setLiveZoomIndex] = useState(DEFAULT_LIVE_ZOOM_INDEX);
  // Historical intraday bars backfilling the live window, keyed by
  // position + timeframe so stale bars never render against a newly
  // selected position or zoom level.
  const [intradayBackfill, setIntradayBackfill] = useState<{
    key: string;
    bars: ChartBar[];
  } | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const visiblePositions = useMemo(
    () =>
      positions.filter((position) => {
        if (accountFilter === "all") return true;
        const display = accountDisplayFor(position.brokerAccountId, accounts);
        if (accountFilter === "live") return display.badgeText === "LIVE";
        if (accountFilter === "paper") return display.badgeText === "PAPER";
        if (accountFilter === "forex") {
          // Explicit classification only — no forex symbol heuristic
          // exists (lib/assetClass.ts explains why).
          return (
            position.assetClass === "forex" ||
            display.assetClass === "forex"
          );
        }
        return (
          display.badgeText === "CRYPTO PAPER" ||
          position.assetClass === "crypto" ||
          display.assetClass === "crypto" ||
          isCryptoPositionSymbol(position.sym)
        );
      }),
    [accountFilter, accounts, positions],
  );

  const selectedPosition = useMemo(
    () =>
      visiblePositions.find((p) => positionKey(p) === selectedPositionKey) ??
      null,
    [selectedPositionKey, visiblePositions],
  );

  // Chart fetch on selected position, then keep levels/markers fresh.
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    if (!selectedPosition) {
      setChart(null);
      return;
    }
    const load = async (clear: boolean) => {
      if (inFlight) return;
      inFlight = true;
      if (clear) setChart(null);
      try {
        const snap = await ds.getChartSnapshot(selectedPosition.sym);
        if (!cancelled) setChart(snap);
      } catch {
        if (!cancelled && clear) setChart(null);
      } finally {
        inFlight = false;
      }
    };
    void load(true);
    const id = window.setInterval(() => void load(false), LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ds, selectedPosition]);

  // Intraday backfill: on selection / zoom-window change, fetch real
  // historical bars at the window's backfill timeframe so the live
  // window shows actual price movement, not just the seconds since
  // page load. Refreshed once per bar interval; the 1s live poll
  // keeps the last bucket moving in between. Failure or an empty
  // table (e.g. crypto symbols without intraday rows) degrades to
  // the live-poll-only behavior.
  useEffect(() => {
    let cancelled = false;
    if (!selectedPosition) {
      setIntradayBackfill(null);
      return;
    }
    const zoom = LIVE_ZOOM_WINDOWS[liveZoomIndex];
    const key = backfillKeyFor(selectedPosition, zoom.backfillTimeframe);
    const lookback = Math.min(
      1000,
      Math.max(20, Math.ceil(zoom.seconds / zoom.bucketSeconds) + 10),
    );
    const load = async () => {
      try {
        const snap = await ds.getChartSnapshot(
          selectedPosition.sym,
          lookback,
          zoom.backfillTimeframe,
        );
        if (cancelled) return;
        // Trust only snapshots echoed at the requested intraday
        // timeframe — a source that ignores the timeframe argument
        // would otherwise leak daily bars into the live window.
        const bars =
          snap.timeframe === zoom.backfillTimeframe ? snap.bars : [];
        setIntradayBackfill({ key, bars });
      } catch {
        if (!cancelled) setIntradayBackfill({ key, bars: [] });
      }
    };
    void load();
    const id = window.setInterval(
      () => void load(),
      zoom.bucketSeconds * 1000,
    );
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ds, selectedPosition, liveZoomIndex]);

  // Live P&L poll. Crypto positions are 24/7, so do not gate this on the
  // equity market_open flag.
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const snap = await ds.getLivePnL();
        if (!cancelled) setLivePnL(snap);
      } catch {
        // Network failure: keep last-known value, do not zero it.
      } finally {
        inFlight = false;
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), LIVE_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ds]);

  // Re-default selection if the chosen position vanishes from the filtered list.
  useEffect(() => {
    if (
      selectedPositionKey &&
      visiblePositions.some((p) => positionKey(p) === selectedPositionKey)
    ) {
      return;
    }
    setSelectedPositionKey(
      visiblePositions.length > 0 ? positionKey(visiblePositions[0]) : null,
    );
  }, [selectedPositionKey, visiblePositions]);

  const livePnLByPositionKey = useMemo(() => {
    const m = new Map<string, LivePnLEntry>();
    if (livePnL) {
      for (const e of livePnL.entries) {
        m.set(`${e.brokerAccountId}:${e.symbol.toUpperCase()}`, e);
      }
    }
    return m;
  }, [livePnL]);

  const livePnLBySymbol = useMemo(() => {
    const m = new Map<string, LivePnLEntry[]>();
    if (livePnL) {
      for (const e of livePnL.entries) {
        const key = e.symbol.toUpperCase();
        m.set(key, [...(m.get(key) ?? []), e]);
      }
    }
    return m;
  }, [livePnL]);

  const liveForPosition = useCallback(
    (position: Position): LivePnLEntry | null => {
      const exact = livePnLByPositionKey.get(positionKey(position));
      if (exact) return exact;
      const sameSymbol = livePnLBySymbol.get(position.sym.toUpperCase()) ?? [];
      return sameSymbol.length === 1 ? sameSymbol[0] : null;
    },
    [livePnLByPositionKey, livePnLBySymbol],
  );

  const selectedLive = selectedPosition ? liveForPosition(selectedPosition) : null;
  const selectedReassessment = selectedPosition
    ? positionReassessments.find(
        (row) =>
          row.status === "active" &&
          row.brokerAccountId === selectedPosition.brokerAccountId &&
          row.symbol.toUpperCase() === selectedPosition.sym.toUpperCase(),
      ) ?? null
    : null;
  useEffect(() => {
    if (!selectedPosition || selectedLive?.currentPrice == null || !livePnL) {
      return;
    }
    const symbol = positionKey(selectedPosition);
    const tickTime = Math.floor(Date.parse(livePnL.asOf) / 1000);
    const nowSec = Number.isFinite(tickTime)
      ? tickTime
      : Math.floor(Date.now() / 1000);
    const price = selectedLive.currentPrice;
    setLiveBarsBySymbol((prev) => {
      const existing = prev[symbol] ?? [];
      const pruned = existing.filter(
        (bar) =>
          Math.floor(Date.parse(bar.time) / 1000) >=
          nowSec - MAX_LIVE_WINDOW_SECONDS,
      );
      const nextBar = {
        time: new Date(nowSec * 1000).toISOString(),
        open: price,
        high: price,
        low: price,
        close: price,
        volume: 0,
      };
      const last = pruned.at(-1);
      const bars =
        last && last.time === nextBar.time
          ? [...pruned.slice(0, -1), nextBar]
          : [...pruned, nextBar];
      return { ...prev, [symbol]: bars };
    });
  }, [livePnL, selectedLive, selectedPosition]);

  const selectedLiveBars = selectedPosition
    ? liveBarsBySymbol[positionKey(selectedPosition)] ?? []
    : [];
  const liveZoom = LIVE_ZOOM_WINDOWS[liveZoomIndex];
  const liveWindowSeconds = liveZoom.seconds;
  const backfillBars =
    selectedPosition &&
    intradayBackfill &&
    intradayBackfill.key ===
      backfillKeyFor(selectedPosition, liveZoom.backfillTimeframe)
      ? intradayBackfill.bars
      : [];
  const displayChart = useMemo(
    () =>
      withLiveWindow(
        chart,
        selectedPosition,
        selectedLive,
        selectedLiveBars,
        backfillBars,
        liveWindowSeconds,
        liveZoom.bucketSeconds,
      ),
    [
      chart,
      selectedPosition,
      selectedLive,
      selectedLiveBars,
      backfillBars,
      liveWindowSeconds,
      liveZoom.bucketSeconds,
    ],
  );

  const runAction = useCallback(
    async (action: PendingAction) => {
      if (!selectedPosition) return;
      const brokerAccountId = selectedPosition.brokerAccountId;
      if (!brokerAccountId) {
        setActionError("No broker_account_id resolved for this symbol.");
        return;
      }
      const idempotencyKey = `${action.kind}-${selectedPosition.sym}-${Date.now()}`;
      setActionError(null);
      setActionMessage("Submitting…");
      try {
        if (action.kind === "tighten_stop") {
          const out = await ds.tightenStop({
            brokerAccountId,
            symbol: selectedPosition.sym,
            newStopPrice: action.newStop,
            preset: action.preset,
            idempotencyKey,
          });
          setActionMessage(
            `Stop moved ${out.oldPrice} → ${out.newPrice}`,
          );
        } else if (action.kind === "adjust_target") {
          const out = await ds.adjustTarget({
            brokerAccountId,
            symbol: selectedPosition.sym,
            newTargetPrice: action.newTarget,
            idempotencyKey,
          });
          setActionMessage(
            `Target moved ${out.oldPrice} → ${out.newPrice}` +
              (out.loosened ? " (loosened)" : ""),
          );
        } else if (action.kind === "break_even") {
          const out = await ds.breakEven({
            brokerAccountId,
            symbol: selectedPosition.sym,
            idempotencyKey,
          });
          setActionMessage(`Stop moved to break-even (${out.newPrice})`);
        } else if (action.kind === "exit_at_market") {
          const out = await ds.exitAtMarket({
            brokerAccountId,
            symbol: selectedPosition.sym,
            idempotencyKey,
          });
          setActionMessage(`Exit submitted for ${out.qty}`);
        } else if (action.kind === "exit_at_limit") {
          const out = await ds.exitAtLimit({
            brokerAccountId,
            symbol: selectedPosition.sym,
            limitPrice: action.limitPrice,
            idempotencyKey,
          });
          setActionMessage(
            `Limit exit submitted for ${out.qty} @ ${out.limitPrice}`,
          );
        } else if (action.kind === "adjust_qty") {
          const out = await ds.adjustPositionQty({
            brokerAccountId,
            symbol: selectedPosition.sym,
            targetRiskDollars: action.targetRiskDollars,
            idempotencyKey,
          });
          setActionMessage(
            `${out.action === "add" ? "Add" : "Trim"} submitted: ${out.oldQty} → ${out.newQty} sh` +
              (out.exitResizeWarning
                ? `; exit resize warning: ${out.exitResizeWarning}`
                : ""),
          );
        }
      } catch (exc: unknown) {
        const detail =
          exc && typeof exc === "object" && "message" in exc
            ? String((exc as { message?: string }).message)
            : "Action failed";
        setActionError(detail);
        setActionMessage(null);
      } finally {
        setPending(null);
      }
    },
    [ds, selectedPosition],
  );

  const runReassessment = useCallback(async () => {
    if (!selectedPosition) return;
    if (!ds.runPositionReassessment) {
      setActionError("Position reassessment is not available.");
      return;
    }
    setActionError(null);
    setActionMessage("Reviewing position…");
    try {
      const out = await ds.runPositionReassessment({
        brokerAccountId: selectedPosition.brokerAccountId,
        symbol: selectedPosition.sym,
        includeEquity: true,
        includeCrypto: true,
        includeForex: true,
        persist: true,
      });
      setActionMessage(
        `Position review complete: ${out.reviewed} reviewed, ${out.persisted} saved`,
      );
    } catch (exc: unknown) {
      const detail =
        exc && typeof exc === "object" && "message" in exc
          ? String((exc as { message?: string }).message)
          : "Position review failed";
      setActionError(detail);
      setActionMessage(null);
    }
  }, [ds, selectedPosition]);

  const confirmReassessment = useCallback(
    async (row: PositionReassessment) => {
      setActionError(null);
      setActionMessage("Confirming reassessment action…");
      try {
        if (!ds.confirmPositionReassessmentAction) {
          throw new Error("Position reassessment is not available.");
        }
        await ds.confirmPositionReassessmentAction({
          reassessmentId: row.reassessmentId,
          idempotencyKey: `reassessment-${row.reassessmentId}-${Date.now()}`,
        });
        setActionMessage("Reassessment action submitted");
      } catch (exc: unknown) {
        const detail =
          exc && typeof exc === "object" && "message" in exc
            ? String((exc as { message?: string }).message)
            : "Reassessment action failed";
        setActionError(detail);
        setActionMessage(null);
      }
    },
    [ds],
  );

  const dismissReassessment = useCallback(
    async (row: PositionReassessment) => {
      setActionError(null);
      try {
        if (!ds.dismissPositionReassessment) {
          throw new Error("Position reassessment is not available.");
        }
        await ds.dismissPositionReassessment({
          reassessmentId: row.reassessmentId,
          note: "dismissed in day trades",
        });
        setActionMessage("Reassessment dismissed");
      } catch (exc: unknown) {
        const detail =
          exc && typeof exc === "object" && "message" in exc
            ? String((exc as { message?: string }).message)
            : "Dismiss failed";
        setActionError(detail);
      }
    },
    [ds],
  );

  return (
    <div data-screen-label="04 Day trades">
      <div className="tp-page-head">
        <h1>Day trades</h1>
        <div className="meta">
          {visiblePositions.length} of {positions.length} open broker position
          {positions.length === 1 ? "" : "s"}
          {livePnL && (
            <span className="tp-muted" style={{ marginLeft: 8 }}>
              · live P&L{" "}
              {positions.some((p) => positionQtyClass(p) === "crypto")
                ? "(crypto 24/7)"
                : positions.some((p) => positionQtyClass(p) === "forex")
                  ? "(forex 24/5 · approx)"
                  : livePnL.marketOpen
                    ? "(market open)"
                    : "(market closed)"}
            </span>
          )}
        </div>
      </div>

      <div className="tp-actionbar" style={{ marginBottom: 14 }}>
        {[
          ["all", "All trading"],
          ["live", "Live"],
          ["paper", "Paper"],
          ["crypto", "Crypto"],
          ["forex", "Forex"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={
              "tp-chip-btn" + (accountFilter === value ? " active" : "")
            }
            onClick={() =>
              setAccountFilter(
                value as "all" | "live" | "paper" | "crypto" | "forex",
              )
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
          gap: 14,
        }}
      >
        <PositionsTable
          accounts={accounts}
          positions={visiblePositions}
          selectedPositionKey={selectedPositionKey}
          onSelect={setSelectedPositionKey}
          liveForPosition={liveForPosition}
        />

        <PositionDetailPanel
          accountDisplay={
            selectedPosition
              ? accountDisplayFor(selectedPosition.brokerAccountId, accounts)
              : null
          }
          position={selectedPosition}
          reassessment={selectedReassessment}
          chart={displayChart}
          live={selectedLive}
          liveWindowLabel={LIVE_ZOOM_WINDOWS[liveZoomIndex].label}
          liveWindowSeconds={liveWindowSeconds}
          canZoomIn={liveZoomIndex > 0}
          canZoomOut={liveZoomIndex < LIVE_ZOOM_WINDOWS.length - 1}
          onZoomIn={() => setLiveZoomIndex((i) => Math.max(0, i - 1))}
          onZoomOut={() =>
            setLiveZoomIndex((i) =>
              Math.min(LIVE_ZOOM_WINDOWS.length - 1, i + 1),
            )
          }
          onAction={(a) => setPending(a)}
          onRunReassessment={runReassessment}
          onConfirmReassessment={confirmReassessment}
          onDismissReassessment={dismissReassessment}
          pending={pending}
          onConfirm={runAction}
          onCancel={() => setPending(null)}
          actionMessage={actionMessage}
          actionError={actionError}
        />
      </div>
    </div>
  );
};

function backfillKeyFor(
  position: Pick<Position, "brokerAccountId" | "sym">,
  timeframe: ChartTimeframe,
): string {
  return `${positionKey(position)}:${timeframe}`;
}

/** Merge, on one uniform bucket grid sized `bucketSeconds`:
 *  1. historical intraday bars (server backfill) within the window,
 *  2. the in-memory live-poll points accumulated since page load,
 *  3. the current live price (always lands in the newest bucket).
 * Uniform buckets matter: the renderer (lightweight-charts) spaces
 * bars by INDEX, not wall-clock time, so mixing 1s poll points with
 * 5m history would distort the x-axis. */
function withLiveWindow(
  chart: ChartSnapshot | null,
  position: Position | null,
  live: LivePnLEntry | null,
  liveBars: ChartBar[],
  backfillBars: ChartBar[],
  windowSeconds: number,
  bucketSeconds: number,
): ChartSnapshot | null {
  if (
    !chart ||
    live?.currentPrice === null ||
    live?.currentPrice === undefined ||
    !Number.isFinite(live.currentPrice) ||
    (chart.bars.length === 0 && backfillBars.length === 0)
  ) {
    return chart;
  }
  const parsedLast = Date.parse(liveBars.at(-1)?.time ?? "");
  const nowSec = Number.isFinite(parsedLast)
    ? Math.floor(parsedLast / 1000)
    : Math.floor(Date.now() / 1000);
  const startSec = nowSec - windowSeconds;

  const byBucket = new Map<number, ChartBar>();
  const merge = (bucketSec: number, bar: ChartBar) => {
    if (bucketSec < startSec - bucketSeconds || bucketSec > nowSec) return;
    const existing = byBucket.get(bucketSec);
    if (!existing) {
      byBucket.set(bucketSec, {
        ...bar,
        time: new Date(bucketSec * 1000).toISOString(),
      });
      return;
    }
    byBucket.set(bucketSec, {
      time: existing.time,
      open: existing.open,
      high: Math.max(existing.high, bar.high),
      low: Math.min(existing.low, bar.low),
      close: bar.close,
      volume: existing.volume + bar.volume,
    });
  };

  // 1. Server backfill first (authoritative for its interval). A
  // historical bar's `time` is its CLOSE instant, so the bar covers
  // (time - bucketSeconds, time] — assign it to the bucket it covers
  // rather than the bucket it merely touches at second 0.
  for (const bar of backfillBars) {
    const sec = Math.floor(Date.parse(bar.time) / 1000);
    if (!Number.isFinite(sec)) continue;
    merge(Math.floor((sec - 1) / bucketSeconds) * bucketSeconds, bar);
  }
  // 2 + 3. Live-poll points and the current price are point-in-time
  // observations: floor onto the bucket grid, updating the last
  // bucket's close/high/low instead of appending per-poll points.
  const putPoint = (sec: number, price: number) => {
    if (!Number.isFinite(sec) || sec < startSec || sec > nowSec) return;
    merge(Math.floor(sec / bucketSeconds) * bucketSeconds, {
      time: new Date(sec * 1000).toISOString(),
      open: price,
      high: price,
      low: price,
      close: price,
      volume: 0,
    });
  };
  for (const bar of liveBars) {
    putPoint(Math.floor(Date.parse(bar.time) / 1000), bar.close);
  }
  putPoint(nowSec, live.currentPrice);

  const bars = Array.from(byBucket.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, bar]) => bar);
  return {
    ...chart,
    bars,
    overlays: { sma20: [], sma50: [], sma200: [], rsi14: [] },
    levels: mergePositionLevels(chart.levels, position, live),
    markers: [],
    trades: [],
  };
}

function mergePositionLevels(
  levels: ChartLevel[],
  position: Position | null,
  live: LivePnLEntry,
): ChartLevel[] {
  const merged = [...levels];
  const add = (
    kind: ChartLevel["kind"],
    price: number,
    label: string,
    status: string,
  ) => {
    if (!Number.isFinite(price) || price <= 0) return;
    const key = `${kind}:${price.toFixed(8)}`;
    if (merged.some((level) => `${level.kind}:${level.price.toFixed(8)}` === key)) {
      return;
    }
    merged.push({ id: key, kind, price, label, status });
  };
  add("entry", live.avgEntryPrice, "entry", "open");
  if (position) {
    add("stop", position.stop, "stop", position.stopStatus ?? "working");
    add(
      "target",
      position.target,
      "target",
      position.targetStatus ?? "working",
    );
  }
  return merged;
}

function isCryptoPositionSymbol(symbol: string): boolean {
  const normalized = symbol.replace("/", "").replace("-", "").toUpperCase();
  for (const quote of ["USDC", "USDT", "USD"]) {
    if (normalized.endsWith(quote)) {
      return CRYPTO_BASES.has(normalized.slice(0, -quote.length));
    }
  }
  return false;
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

const PositionsTable = ({
  accounts,
  positions,
  selectedPositionKey,
  onSelect,
  liveForPosition,
}: {
  accounts: Account[];
  positions: Position[];
  selectedPositionKey: string | null;
  onSelect: (key: string) => void;
  liveForPosition: (position: Position) => LivePnLEntry | null;
}) => (
  <div className="tp-tile">
    <div className="tp-tile-head">
      <span className="label">Open day/swing positions</span>
      <span className="tp-muted">source: broker</span>
    </div>
    <div className="tp-tile-body" style={{ padding: 0 }}>
      <table className="tp-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th className="right">Qty</th>
            <th className="right">Entry</th>
            <th className="right">Last</th>
            <th className="right">P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {positions.length === 0 ? (
            <tr>
              <td colSpan={5} className="tp-empty-cell">
                No open broker positions.
              </td>
            </tr>
          ) : (
            positions.map((p) => {
              const live = liveForPosition(p);
              const pnl = live?.unrealizedPnL ?? p.pl;
              const pnlPct = live?.unrealizedPnLPct ?? p.plPct;
              const last = live?.currentPrice ?? p.last;
              const key = positionKey(p);
              const display = accountDisplayFor(p.brokerAccountId, accounts);
              const isSelected = key === selectedPositionKey;
              return (
                <tr
                  key={key}
                  onClick={() => onSelect(key)}
                  style={{
                    cursor: "pointer",
                    background: isSelected
                      ? "rgba(45, 107, 255, 0.10)"
                      : undefined,
                  }}
                >
                  <td className="ticker">
                    <SymbolLabel symbol={p.sym} companyName={p.companyName} />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 4,
                      }}
                    >
                      <AccountBadge display={display} />
                      <span className="tp-row-sub">{display.label}</span>
                    </div>
                    {p.strategy && (
                      <div className="tp-row-sub">{p.strategy}</div>
                    )}
                  </td>
                  <td className="num right">
                    {p.qty}
                    <div className="tp-row-sub">
                      {positionSideLabel(p).toLowerCase()}
                    </div>
                  </td>
                  <td className="num right">{fmtPx(p.entry)}</td>
                  <td className="num right">{fmtPx(last)}</td>
                  <td className={"num right " + (pnl >= 0 ? "pos" : "neg")}>
                    {fmtSigned(Math.round(pnl))}
                    <div className="tp-row-sub">{fmtPct(pnlPct)}</div>
                    {live?.stale && (
                      <div className="tp-row-sub tp-muted">stale</div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const PositionDetailPanel = ({
  accountDisplay,
  position,
  reassessment,
  chart,
  live,
  liveWindowLabel,
  liveWindowSeconds,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onAction,
  onRunReassessment,
  onConfirmReassessment,
  onDismissReassessment,
  pending,
  onConfirm,
  onCancel,
  actionMessage,
  actionError,
}: {
  accountDisplay: ReturnType<typeof accountDisplayFor> | null;
  position: Position | null;
  reassessment: PositionReassessment | null;
  chart: ChartSnapshot | null;
  live: LivePnLEntry | null;
  liveWindowLabel: string;
  liveWindowSeconds: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onAction: (a: PendingAction) => void;
  onRunReassessment: () => void;
  onConfirmReassessment: (row: PositionReassessment) => void;
  onDismissReassessment: (row: PositionReassessment) => void;
  pending: PendingAction | null;
  onConfirm: (a: PendingAction) => void;
  onCancel: () => void;
  actionMessage: string | null;
  actionError: string | null;
}) => {
  if (!position) {
    return (
      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Position detail</span>
        </div>
        <div className="tp-tile-body" style={{ padding: 24 }}>
          <p className="tp-muted">
            Click a row on the left to focus a position. The detail panel
            shows the chart, live P&L, and the four UX §9.2 actions.
          </p>
        </div>
      </div>
    );
  }
  const currentPrice = live?.currentPrice ?? position.last;
  const entry = position.entry;
  const inProfit = isPositionInProfit(position, currentPrice);
  const stopOutcome = positionOutcomeAtExit(position, position.stop);
  const targetOutcome = positionOutcomeAtExit(position, position.target);
  return (
    <div className="tp-tile">
      <div className="tp-tile-head">
        <div>
          <span className="label">{position.sym} — position detail</span>
          {accountDisplay ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 4,
              }}
            >
              <AccountBadge display={accountDisplay} />
              <span className="tp-muted">{accountDisplay.label}</span>
            </div>
          ) : null}
          {position.companyName ? (
            <div className="tp-symbol-company">{position.companyName}</div>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="tp-muted">{position.strategy ?? ""}</span>
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            onClick={onRunReassessment}
          >
            Review position
          </button>
        </div>
      </div>
      <div className="tp-tile-body" style={{ padding: 14 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <Metric
            label="Qty"
            value={formatQty(position.qty, position)}
            subValue={positionSideLabel(position)}
          />
          <Metric label="Entry" value={fmtPx(entry)} />
          <Metric label="Last" value={fmtPx(currentPrice)} />
          <Metric
            label="P&L"
            value={
              live
                ? `${fmtSigned(Math.round(live.unrealizedPnL))} (${fmtPct(live.unrealizedPnLPct)})`
                : `${fmtSigned(Math.round(position.pl))} (${fmtPct(position.plPct)})`
            }
            tone={
              (live?.unrealizedPnL ?? position.pl) >= 0 ? "pos" : "neg"
            }
          />
          <Metric
            label="Stop loss"
            value={fmtMaybePx(position.stop)}
            subValue={position.stopStatus ?? "not linked"}
            tone={position.stop > 0 ? undefined : "neg"}
          />
          <Metric
            label="Target"
            value={fmtMaybePx(position.target)}
            subValue={position.targetStatus ?? "not linked"}
          />
          <Metric
            label="If stop fills"
            value={fmtUSD(stopOutcome)}
            tone={
              stopOutcome < 0
                ? "neg"
                : stopOutcome > 0
                  ? "pos"
                  : undefined
            }
          />
          <Metric
            label="If target fills"
            value={fmtUSD(targetOutcome)}
            tone={
              targetOutcome > 0
                ? "pos"
                : targetOutcome < 0
                  ? "neg"
                  : undefined
            }
          />
        </div>

        <ReassessmentPanel
          row={reassessment}
          onConfirm={onConfirmReassessment}
          onDismiss={onDismissReassessment}
        />

        <ActionBar
          position={position}
          currentPrice={currentPrice}
          inProfit={inProfit}
          onAction={onAction}
          pending={pending}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span className="tp-muted">Live window {liveWindowLabel}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              onClick={onZoomOut}
              disabled={!canZoomOut}
              title="Zoom out"
              aria-label="Zoom out"
              style={{ width: 34, paddingInline: 0 }}
            >
              -
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              onClick={onZoomIn}
              disabled={!canZoomIn}
              title="Zoom in"
              aria-label="Zoom in"
              style={{ width: 34, paddingInline: 0 }}
            >
              +
            </button>
          </div>
        </div>

        {chart && chart.bars.length > 0 ? (
          <PriceChart
            snapshot={chart}
            showSma={false}
            showRsi={false}
            showMarkers={false}
            showLevels
            fitKey={0}
            mode="line"
            visibleWindowSeconds={liveWindowSeconds}
          />
        ) : chart ? (
          <div
            style={{
              height: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
            className="tp-muted"
          >
            No chart data for {position.sym}.
          </div>
        ) : (
          <div
            style={{
              height: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className="tp-muted"
          >
            Loading chart…
          </div>
        )}

        {actionError && (
          <div className="tp-banner bad" style={{ marginTop: 12 }}>
            <span className="icon">!</span>
            <span>{actionError}</span>
          </div>
        )}
        {actionMessage && !actionError && (
          <div className="tp-banner info" style={{ marginTop: 12 }}>
            <span className="icon">✓</span>
            <span>{actionMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ReassessmentPanel = ({
  row,
  onConfirm,
  onDismiss,
}: {
  row: PositionReassessment | null;
  onConfirm: (row: PositionReassessment) => void;
  onDismiss: (row: PositionReassessment) => void;
}) => {
  if (!row) {
    return (
      <div className="tp-position-action-card" style={{ marginBottom: 14 }}>
        <div className="tp-position-action-head">Position review</div>
        <div className="tp-position-action-note">
          No active reassessment for this position.
        </div>
      </div>
    );
  }
  const anthropic = summaryFromReview(row.llmDebate.anthropic_review);
  const codex = summaryFromReview(row.llmDebate.codex_review);
  const reasons = Array.isArray(row.deterministicResult.reasons)
    ? row.deterministicResult.reasons.map(String)
    : [];
  const canConfirm = row.proposedAction !== "none";
  return (
    <div className="tp-position-action-card" style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "start",
        }}
      >
        <div>
          <div className="tp-position-action-head">Position review</div>
          <div style={{ fontSize: 13 }}>
            {formatRecommendation(row.recommendation)} ·{" "}
            {formatRecommendation(row.proposedAction)} · {row.severity}
          </div>
          <div className="tp-position-action-note">
            expires {new Date(row.expiresAt).toLocaleTimeString()}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            onClick={() => onDismiss(row)}
          >
            Dismiss
          </button>
          <button
            type="button"
            className="tp-btn tp-btn-primary tp-btn-sm"
            disabled={!canConfirm}
            title={
              canConfirm
                ? "Confirm through deterministic position checks"
                : "No broker action is attached to this recommendation"
            }
            onClick={() => onConfirm(row)}
          >
            Confirm action
          </button>
        </div>
      </div>
      {reasons.length > 0 && (
        <div className="tp-position-action-note" style={{ marginTop: 8 }}>
          {reasons.slice(0, 2).join(" · ")}
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 8,
          marginTop: 10,
        }}
      >
        <ReviewVoice label="Anthropic" text={anthropic} />
        <ReviewVoice label="Codex" text={codex} />
      </div>
      {row.blockers.length > 0 && (
        <div className="tp-banner bad" style={{ marginTop: 10 }}>
          <span className="icon">!</span>
          <span>{row.blockers.map((b) => String(b.code)).join(", ")}</span>
        </div>
      )}
    </div>
  );
};

const ReviewVoice = ({ label, text }: { label: string; text: string }) => (
  <div>
    <div className="tp-position-action-head">{label}</div>
    <div className="tp-position-action-note">{text || "not available"}</div>
  </div>
);

function summaryFromReview(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  if (typeof record.summary === "string") return record.summary;
  if (typeof record.recommendation === "string") return record.recommendation;
  return "";
}

function formatRecommendation(value: string): string {
  return value.replace(/_/g, " ");
}

const ActionBar = ({
  position,
  currentPrice,
  inProfit,
  onAction,
  pending,
  onConfirm,
  onCancel,
}: {
  position: Position;
  currentPrice: number;
  inProfit: boolean;
  onAction: (a: PendingAction) => void;
  pending: PendingAction | null;
  onConfirm: (a: PendingAction) => void;
  onCancel: () => void;
}) => {
  const [customStop, setCustomStop] = useState("");
  const [customTarget, setCustomTarget] = useState("");
  const [targetRiskDollars, setTargetRiskDollars] = useState("");
  const [limitExitPrice, setLimitExitPrice] = useState(() =>
    editablePrice(position.last),
  );
  useEffect(() => {
    setLimitExitPrice(editablePrice(position.last));
    setTargetRiskDollars(currentPositionRisk(position, currentPrice).toFixed(2));
  }, [currentPrice, position]);
  if (pending) {
    return (
      <ConfirmActionPanel
        position={position}
        action={pending}
        onConfirm={() => onConfirm(pending)}
        onCancel={onCancel}
      />
    );
  }
  const longActionOnlyTitle =
    "Manual stop and target changes are currently supported for long positions only.";
  const stopLinked = Number.isFinite(position.stop) && position.stop > 0;
  const targetLinked = Number.isFinite(position.target) && position.target > 0;
  const stopActive = stopLinked && isActiveExitStatus(position.stopStatus);
  const targetActive = targetLinked && isActiveExitStatus(position.targetStatus);
  const canAdjustQty = stopActive && targetActive;
  const stopActionsSupported = position.side === "long";
  const canUseStopActions = stopActionsSupported && stopActive;
  const canUseTargetActions = stopActionsSupported && targetActive;
  const limitExitSupported = position.side === "long";
  const unit = positionUnit(position);
  const qtyStep = positionQtyClass(position) === "crypto" ? "0.00000001" : "1";
  const validLimitExit =
    limitExitSupported &&
    limitExitPrice.trim() !== "" &&
    Number(limitExitPrice) > 0;
  const stopUnavailableTitle = !stopLinked
    ? "No linked stop order was found for this position."
    : !stopActive
      ? `Linked stop is ${position.stopStatus}; repair the stop before editing this position.`
      : undefined;
  const targetUnavailableTitle = !targetLinked
    ? "No linked target order was found for this position."
    : !targetActive
      ? `Linked target is ${position.targetStatus}; repair the target before editing this position.`
      : undefined;
  const stopActionTitle = !stopActionsSupported
    ? longActionOnlyTitle
    : stopUnavailableTitle;
  const targetActionTitle = !stopActionsSupported
    ? longActionOnlyTitle
    : targetUnavailableTitle;
  const adjustQtyTitle = canAdjustQty
    ? "Set total position size from dollar risk"
    : [stopUnavailableTitle, targetUnavailableTitle].filter(Boolean).join(" ");
  const targetRiskNumber = Number(targetRiskDollars);
  const adjustPreview = previewTargetQty(position, currentPrice, targetRiskNumber);
  const adjustNoop =
    adjustPreview !== null && adjustPreview.newQty === position.qty;
  const adjustQtyButtonTitle = !canAdjustQty
    ? adjustQtyTitle
    : adjustNoop
      ? "Target risk maps to the current quantity."
      : adjustQtyTitle;
  return (
    <div className="tp-position-action-grid">
      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Edit qty by risk</div>
        <label
          htmlFor={`target-risk-${position.sym}`}
          className="tp-muted"
          style={{
            display: "block",
            fontSize: 11,
            marginBottom: 4,
            textTransform: "uppercase",
          }}
        >
          Dollar risk
        </label>
        <div style={{ display: "flex", gap: 4 }}>
          <input
            id={`target-risk-${position.sym}`}
            type="number"
            min={positionQtyClass(position) === "crypto" ? "0.00000001" : "1"}
            step={qtyStep}
            placeholder="risk $"
            aria-label="Target dollar risk"
            className="tp-input"
            value={targetRiskDollars}
            onChange={(e) => setTargetRiskDollars(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            disabled={
              !canAdjustQty ||
              !Number.isFinite(targetRiskNumber) ||
              targetRiskNumber <= 0 ||
              adjustPreview === null ||
              adjustNoop
            }
            title={adjustQtyButtonTitle}
            onClick={() =>
              onAction({
                kind: "adjust_qty",
                targetRiskDollars,
              })
            }
          >
            Set
          </button>
        </div>
        <div
          className="tp-position-action-note"
          style={!canAdjustQty ? { color: "var(--tp-warn)" } : undefined}
        >
          {!canAdjustQty
            ? adjustQtyTitle
            : adjustPreview
            ? `${adjustPreview.action} to ${formatQty(
                adjustPreview.newQty,
                position,
              )} ${unit} · gain ${fmtUSD(
                adjustPreview.targetGain,
              )}`
            : "Enter dollar risk to preview add/trim."}
        </div>
      </div>
      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Tighten stop</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={!canUseStopActions}
              title={stopActionTitle}
              onClick={() => {
                const stop = computePresetStop(p.key, position);
                if (stop === null) return;
                onAction({
                  kind: "tighten_stop",
                  preset: p.key,
                  newStop: stop,
                });
              }}
            >
              {p.label}
            </button>
          ))}
          <div style={{ display: "flex", gap: 4 }}>
            <input
              type="text"
              placeholder="custom"
              className="tp-input"
              value={customStop}
              onChange={(e) => setCustomStop(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={!customStop || !canUseStopActions}
              title={stopActionTitle}
              onClick={() =>
                onAction({
                  kind: "tighten_stop",
                  preset: "custom",
                  newStop: customStop,
                })
              }
            >
              Set
            </button>
          </div>
        </div>
      </div>

      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Adjust target</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input
            type="text"
            placeholder="new target"
            className="tp-input"
            value={customTarget}
            onChange={(e) => setCustomTarget(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            disabled={!customTarget || !canUseTargetActions}
            title={targetActionTitle}
            onClick={() =>
              onAction({
                kind: "adjust_target",
                newTarget: customTarget,
              })
            }
          >
            Set
          </button>
        </div>
      </div>

      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Break even (B)</div>
        <button
          type="button"
          className="tp-btn tp-btn-primary tp-btn-sm"
          disabled={!inProfit || !canUseStopActions}
          title={
            !stopActionsSupported
              ? longActionOnlyTitle
              : (stopUnavailableTitle ??
                (inProfit
                  ? "Move stop to entry price"
                  : "Enabled only when in profit"))
          }
          onClick={() => onAction({ kind: "break_even" })}
        >
          Stop → entry
        </button>
      </div>

      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Exit position (X)</div>
        <button
          type="button"
          className="tp-btn tp-btn-danger tp-btn-sm"
          onClick={() => onAction({ kind: "exit_at_market" })}
        >
          Exit at market
        </button>
      </div>

      <div className="tp-position-action-card">
        <div className="tp-position-action-head">Limit exit</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input
            type="text"
            placeholder="limit price"
            className="tp-input"
            value={limitExitPrice}
            onChange={(e) => setLimitExitPrice(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="tp-btn tp-btn-secondary tp-btn-sm"
            disabled={!validLimitExit}
            title={
              limitExitSupported
                ? undefined
                : "Limit exit is currently sell-only; use market exit to close this short."
            }
            onClick={() =>
              onAction({
                kind: "exit_at_limit",
                limitPrice: limitExitPrice,
              })
            }
          >
            Sell limit
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmActionPanel = ({
  position,
  action,
  onConfirm,
  onCancel,
}: {
  position: Position;
  action: PendingAction;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  let title = "Confirm action";
  let body = "";
  if (action.kind === "tighten_stop") {
    title = "Tighten stop";
    body =
      `Move stop on ${position.sym} from ${fmtMaybePx(position.stop)} to ` +
      `${action.newStop} (${action.preset}).`;
  } else if (action.kind === "adjust_target") {
    title = "Adjust target";
    body =
      `Move target on ${position.sym} from ${fmtMaybePx(position.target)} to ` +
      `${action.newTarget}.`;
  } else if (action.kind === "break_even") {
    title = "Stop → break even";
    body =
      `Move stop on ${position.sym} to entry price ${fmtPx(position.entry)}.`;
  } else if (action.kind === "exit_at_market") {
    title = "Exit at market";
    body =
      `Cancel brackets and submit a market close for ${position.qty} ` +
      `${positionUnit(position)} of ${position.sym}. This action cannot be undone.`;
  } else if (action.kind === "exit_at_limit") {
    title = "Submit limit exit";
    body =
      `Cancel existing sell exits and submit a limit sell for ${position.qty} ` +
      `${positionUnit(position)} of ${position.sym} at ${action.limitPrice}.`;
  } else if (action.kind === "adjust_qty") {
    title = "Edit position quantity";
    body =
      `Resize ${position.sym} using ${fmtUSD(Number(action.targetRiskDollars))} ` +
      "target dollar risk. The broker action will be a market add or trim, " +
      "then linked stop and target orders will be resized.";
  }
  return (
    <div
      className="tp-banner warn"
      style={{ marginTop: 14, alignItems: "stretch", flexDirection: "column" }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ marginBottom: 10 }}>{body}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={onConfirm}
        >
          Confirm
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const Metric = ({
  label,
  value,
  subValue,
  tone,
}: {
  label: string;
  value: string;
  subValue?: string | null;
  tone?: "pos" | "neg";
}) => (
  <div>
    <div className="tp-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
      {label}
    </div>
    <div className={"num " + (tone ?? "")}>{value}</div>
    {subValue && <div className="tp-row-sub">{subValue}</div>}
  </div>
);

function computePresetStop(preset: PresetKey, p: Position): string | null {
  // Phase 5 (d) D2 v1: preset → new_stop_price translation. The
  // server's tighten_stop endpoint validates against current price
  // and current stop, so locally we can be approximate.
  if (!Number.isFinite(p.stop) || p.stop <= 0) return null;
  const risk = p.entry - p.stop;
  if (preset === "break_even") {
    return p.entry.toFixed(2);
  }
  if (preset === "plus_0_5r") {
    return (p.stop + 0.5 * risk).toFixed(2);
  }
  if (preset === "plus_1r") {
    return (p.stop + risk).toFixed(2);
  }
  if (preset === "trailing_2_atr") {
    // Lacking ATR client-side, approximate as stop + 1% of price.
    return Math.max(p.stop + 0.01 * p.last, p.stop).toFixed(2);
  }
  return null;
}
